import { artificeFetch } from '../api/artificeHttp';
/** Where to send users after HL onboarding — avoids looping on /onboarding?source=miniapp&action=... */

export type OnboardingExitContext = {
  source: string | null;
  action: string | null;
};

const SOURCE_KEY = 'jester_onboarding_source';
const ACTION_KEY = 'jester_onboarding_action';

export function readOnboardingExitContext(): OnboardingExitContext {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get('source') || localStorage.getItem(SOURCE_KEY) || null,
      action: params.get('action') || localStorage.getItem(ACTION_KEY) || null,
    };
  } catch {
    return { source: null, action: null };
  }
}

export function persistOnboardingExitContext(source?: string | null, action?: string | null): void {
  try {
    if (source) localStorage.setItem(SOURCE_KEY, source);
    if (action) localStorage.setItem(ACTION_KEY, action);
  } catch {}
}

export function clearOnboardingExitContext(): void {
  try {
    localStorage.removeItem(SOURCE_KEY);
    localStorage.removeItem(ACTION_KEY);
  } catch {}
}

/** Strip loop-prone query params after setup so refresh does not re-open the wizard. */
export function finishOnboardingExit(): void {
  clearOnboardingExitContext();
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete('source');
    u.searchParams.delete('action');
    u.hash = '';
    const qs = u.searchParams.toString();
    window.history.replaceState({}, '', u.pathname + (qs ? `?${qs}` : ''));
  } catch {}
}

let cachedBotUsername: string | null = null;

export async function resolveTelegramBotUsername(): Promise<string> {
  if (cachedBotUsername) return cachedBotUsername;
  try {
    const w = (window as any).TELEGRAM_BOT_USERNAME;
    if (w) {
      cachedBotUsername = String(w).replace(/^@+/, '').trim();
      return cachedBotUsername;
    }
  } catch {}
  try {
    const res = await artificeFetch('/api/public-config', {
      headers: { 'X-Requested-With': 'Jester' },
    });
    const json = await res.json().catch(() => ({}));
    const name = String(json?.telegramBotUsername || '').replace(/^@+/, '').trim();
    if (name) {
      cachedBotUsername = name;
      return name;
    }
  } catch {}
  return '';
}

export function buildTelegramBotUrl(username: string, startapp?: string): string {
  const u = String(username || '').replace(/^@+/, '').trim();
  if (!u) return '';
  if (startapp) return `https://t.me/${u}?startapp=${encodeURIComponent(startapp)}`;
  return `https://t.me/${u}`;
}

export function isInsideTelegramWebApp(): boolean {
  try {
    return !!(window as any).Telegram?.WebApp?.initData;
  } catch {
    return false;
  }
}

export function openExternalUrl(url: string): void {
  const href = String(url || '').trim();
  if (!href) return;
  const tg = (window as any).Telegram?.WebApp;
  try {
    if (tg?.openTelegramLink && href.startsWith('https://t.me/')) {
      tg.openTelegramLink(href);
      return;
    }
    if (tg?.openLink && href.startsWith('http')) {
      tg.openLink(href);
      return;
    }
  } catch {}
  if (href.startsWith('/') || href.startsWith(window.location.origin)) {
    window.location.assign(href.startsWith('/') ? href : href);
    return;
  }
  try {
    window.open(href, '_blank', 'noopener,noreferrer');
  } catch {
    window.location.href = href;
  }
}

export function navigateOnboardingExit(href: string): void {
  const target = String(href || '').trim();
  if (!target) return;
  finishOnboardingExit();
  if (target.startsWith('/')) {
    window.location.assign(target);
    return;
  }
  openExternalUrl(target);
}

export type OnboardingExitTarget = { label: string; href: string };

export function getOnboardingExitTargets(
  ctx: OnboardingExitContext,
  opts: { telegramBotUrl?: string; preferMiniApp?: boolean } = {},
): { primary: OnboardingExitTarget; secondary?: OnboardingExitTarget } {
  const fromOroboros = ctx.source === 'oroboros';
  const fromMiniapp = ctx.source === 'miniapp';
  const inTelegram = isInsideTelegramWebApp();
  const botUrl = opts.telegramBotUrl || '';
  const miniAppHref = '/miniapp';
  const oroborosHref = '/app';

  if (fromOroboros) {
    return {
      primary: { label: 'Return to Oroboros', href: oroborosHref },
      secondary: botUrl
        ? { label: 'Open Telegram Bot', href: botUrl }
        : { label: 'Open Mini App', href: miniAppHref },
    };
  }

  if (fromMiniapp || inTelegram || opts.preferMiniApp) {
    const secondary = botUrl
      ? { label: 'Open Telegram Bot', href: botUrl }
      : undefined;
    return {
      primary: { label: 'Return to Mini App', href: miniAppHref },
      secondary,
    };
  }

  if (botUrl) {
    return {
      primary: { label: 'Open Telegram Bot', href: botUrl },
      secondary: { label: 'Open Mini App', href: miniAppHref },
    };
  }

  return {
    primary: { label: 'Open Mini App', href: miniAppHref },
  };
}
